<template>
    <div class="ai-image-website-content max-z-index" v-show="isShowHandleAi">
		<!-- Show loader while the iframe is loading -->
		<div class="iframe-loading" v-loading="isLoading" v-if="isLoading">
        <!-- <img src="/static/img/ajax-loader.gif" alt="Loading..." /> -->
	</div>
	<!-- Only render iframe when URL is available -->
	<iframe
        v-show="!!handleEnhanceWithAIUrl"
        width="100%"
        @load="onIframeLoad"
        @error="onIframeError"
        :src="handleEnhanceWithAIUrl"
        frameborder="0"
        ref="aiImageIframe"
        id="aiImageIframe"
        class="ai-image-website-iframe"
	></iframe>
    </div>
</template>
<script>
import { WEBAPP_LAYOUT } from "../../../common/constants";
import { storeModules } from "../../../store";
import { AI_URI } from "../../../common/uri/property-uri.constants";
import { AI_APP_ORIGIN } from "../../../common/uri/ai-uri.constants";
import { AI_IMAGE_EVENTS, AI_IMAGE_TYPE, normalizeAISaveMessage } from "../../../common/ai-image.constant";
import { postMessageForWebview } from "../../../utils/web-view";
import { getAuthConfig } from "../../../utils/auth-helper";
import { embeddedAppTokenBroker } from "../../../utils/embedded-app-token";
import { LanguageHelper } from "../../../utils/language-helper";
import {
    createAIImageFlowLogger,
    AIImageFlowStep
} from "../../../utils/graylog-helper";

export default {
    name: 'AIImageWebview',
    data() {
        return {
            aiImageIframe: null,
            handleEnhanceWithAIUrl: '',
            isShowHandleAi: false,
            isLoading: true,
            flowLogger: null, // AI Image Flow Logger instance
            language: null,
            // The native host passes one or more image URLs in `images`; the
            // editor always runs in bulk mode (mode=bulk + init-bulk handshake),
            // even for a single image. Mirrors the WebApp persistent iframe.
            images: [],
            propertyRef: null,
            propertyId: null,
            projectId: null
        }
    },
    computed: {
        webappLayoutMode() {
            return this.$store.state[storeModules.Common].webappLayoutMode;
        },
        isWebview() {
            return this.webappLayoutMode === WEBAPP_LAYOUT.WEBVIEW;
        },
    },
    methods: {
        onIframeLoad() {
            this.isLoading = false;
            if (this.flowLogger) {
                this.flowLogger.log(AIImageFlowStep.IFRAME_LOADED, {
                    iframeUrl: this.handleEnhanceWithAIUrl ? '[LOADED]' : '[EMPTY]'
                });
            }
        },

        onIframeError(error) {
            this.isLoading = false;
            if (this.flowLogger) {
                this.flowLogger.error(AIImageFlowStep.IFRAME_ERROR, error, {
                    iframeUrl: AI_URI.IMAGE_ENHANCEMENT
                });
            }
        },

        async authenticateAndLoadAIImageApp() {
            const hasAccessToken = !!getAuthConfig().oauth2.accessToken;
            // Log auth start
            if (this.flowLogger) {
                this.flowLogger.log(AIImageFlowStep.AUTH_START, {
                    hasAccessToken,
                    imageCount: this.images.length,
                    language: this.language
                });
            }

            try {
				if (!hasAccessToken) {
					console.error("Authentication failed: Missing access token.");
					this.$message.error('Authentication failed: Missing access token.');
                    if (this.flowLogger) {
                        this.flowLogger.error(AIImageFlowStep.AUTH_ERROR, 'Missing access token');
                    }
					this.isLoading = false;
					return;
				}

                // Log auth success
                if (this.flowLogger) {
                    this.flowLogger.log(AIImageFlowStep.AUTH_SUCCESS);
                }

				const sessionId = this.flowLogger?.sessionId || '';

				// Always load the editor in bulk mode â€” images are sent
				// afterwards via init-bulk, right after the ACCESS_TOKEN that
				// answers COMPLETED_LOADING. `minimize=false` keeps minimize
				// disabled in the WebView.
				this.handleEnhanceWithAIUrl = `${AI_URI.IMAGE_ENHANCEMENT}?mode=bulk&language=${this.language}&sessionId=${sessionId}&minimize=false`;

                if (this.flowLogger) {
                    this.flowLogger.log(AIImageFlowStep.IFRAME_LOADING, {
                        targetUrl: AI_URI.IMAGE_ENHANCEMENT,
                        imageCount: this.images.length
                    });
                }
			} catch (error) {
				console.error("Error authenticating and loading image enhancement app:", error);
				this.$message.error('Error authenticating and loading image enhancement app.');
                if (this.flowLogger) {
                    this.flowLogger.error(AIImageFlowStep.AUTH_ERROR, error);
                }
				this.isLoading = false;
			}
		},

		// Hand the editor the bulk selection once the broker answered its COMPLETED_LOADING,
		// mirroring the WebApp persistent iframe's init-bulk handshake. Sends
		// `projectId`/`propertyId` so the editor can associate each result.
		sendInitBulk() {
			if (this.images.length === 0) return;
			const iframe = this.$refs.aiImageIframe;
			if (!iframe || !iframe.contentWindow) return;

			const message = {
				type: 'init-bulk',
				propertyRef: this.propertyRef,
				images: this.images
			};
			if (this.projectId != null) {
				message.projectId = this.projectId;
			} else if (this.propertyId != null) {
				message.propertyId = this.propertyId;
			}

			if (this.flowLogger) {
				this.flowLogger.log(AIImageFlowStep.SESSION_INIT_BULK, {
					imageCount: this.images.length,
					propertyRef: this.propertyRef,
					propertyId: this.propertyId,
					projectId: this.projectId
				});
			}

			iframe.contentWindow.postMessage(message, AI_APP_ORIGIN);
		},

		async handleMessage(event) {
			if (event.origin !== AI_APP_ORIGIN) return;
			const iframe = this.$refs.aiImageIframe;
			if (!iframe || !iframe.contentWindow || event.source !== iframe.contentWindow) return;
			if (!event.data || typeof event.data !== 'object') return;
			const { data } = event;
			const TYPE = data?.TYPE;
			const { type } = data;
			const MessageType = TYPE || type;

            // Log message received from iframe (metadata only, exact image logged at SAVE/REPLACE step)
            if (MessageType && this.flowLogger) {
                this.flowLogger.log(AIImageFlowStep.IFRAME_MESSAGE_RECEIVED, {
                    messageType: MessageType
                });
            }

			switch (MessageType) {
				case AI_IMAGE_EVENTS.CANCEL: {
                    if (this.flowLogger) {
                        this.flowLogger.log(AIImageFlowStep.WINAPP_CANCEL);
                    }
					postMessageForWebview("CANCEL", null, 'AIImageWebview');

                    // Log flow completed
                    if (this.flowLogger) {
                        this.flowLogger.log(AIImageFlowStep.FLOW_COMPLETED, {
                            result: 'CANCELLED'
                        });
                    }
					break;
				}
				// Every save type is forwarded to the native host as one
				// SAVE_ALL shape: images[] + savedJobIds + sessionId (the
				// single `save` is normalized into it). Items carry
				// image/name/imageType/hasWatermark â€” with imageType
				// 'object' the image is the ObjectPicture JSON already
				// uploaded to the Omnicasa cloud; 'base64' is the legacy
				// data-URI fallback. WinApp branches on imageType.
				case AI_IMAGE_EVENTS.SAVE:
				case AI_IMAGE_EVENTS.SAVE_BULK:
				case AI_IMAGE_EVENTS.SAVE_ALL:
				case AI_IMAGE_EVENTS.SAVE_PARTIAL: {
					const { images, savedJobIds, sessionId } = normalizeAISaveMessage(data);
                    if (this.flowLogger) {
                        this.flowLogger.log(AIImageFlowStep.WINAPP_SAVE, {
                            messageType: MessageType,
                            imageCount: images?.length ?? 0,
                            savedJobCount: savedJobIds?.length ?? 0,
                            imageType: images?.[0]?.imageType || AI_IMAGE_TYPE.BASE64,
                            imageUrls: (images || [])
                                .map(img => (typeof img?.image === 'object' ? img.image?.OriginalUrl : null))
                                .filter(Boolean),
                            propertyId: this.propertyId,
                            projectId: this.projectId,
                            propertyRef: this.propertyRef
                        });
                    }
					postMessageForWebview("SAVE_ALL", { images, savedJobIds, sessionId }, 'AIImageWebview');

                    if (this.flowLogger) {
                        this.flowLogger.log(AIImageFlowStep.FLOW_COMPLETED, {
                            result: 'SAVED',
                            messageType: MessageType,
                            imageCount: images?.length ?? 0
                        });
                    }
					break;
				}
				default: {
					break;
				}
			}
		},

		async initData() {
			if (this.isWebview) {
				const vm = window;
				if (vm.chrome && vm.chrome.webview) {
					vm.chrome.webview.addEventListener("message", async (event) => {
						console.log("Listening aiImageInfo");

                        if (event.data.message === "aiImageInfo") {
                            // Create new flow logger for this session
                            this.flowLogger = createAIImageFlowLogger();
                            console.log(`[AIImage] Flow Session ID: ${this.flowLogger.sessionId}`);

                            // Log WINAPP message received
                            this.flowLogger.log(AIImageFlowStep.WINAPP_MESSAGE_RECEIVED, {
                                message: event.data.message,
                                imageCount: Array.isArray(event.data.data?.images) ? event.data.data.images.length : 0,
                                language: event.data.data?.language
                            });

                            const { language, images, propertyRef, propertyId, projectId } = event.data.data;
                            this.language = language;
                            this.images = Array.isArray(images)
                                ? images.filter(url => typeof url === 'string' && url.length > 0)
                                : [];
                            this.propertyRef = propertyRef ?? null;
                            this.propertyId = propertyId ?? null;
                            this.projectId = projectId ?? null;

                            await this.authenticateAndLoadAIImageApp();
                        }
                    });
                }

                console.log('Send message to webview');
                postMessageForWebview("COMPLETE_LOADING", null, 'AIImageWebview');

                // Note: COMPLETE_LOADING is sent before we have a flowLogger
                // This is expected - it's the initial handshake
			}
		}
    },
	async created() {
		this.isShowHandleAi = true;
		this.initData();
	},
	mounted() {
		this.handleMessage = this.handleMessage.bind(this);
		window.addEventListener("message", this.handleMessage);
		this.unregisterAccessToken = embeddedAppTokenBroker.register({
			origin: AI_APP_ORIGIN,
			getContentWindow: () => this.$refs.aiImageIframe?.contentWindow || null,
			getLanguageId: () => LanguageHelper.convertLanguageInt(this.$i18n.locale),
			onCompletedLoading: () => this.sendInitBulk()
		});
	},
	beforeDestroy() {
		window.removeEventListener("message", this.handleMessage);
		this.unregisterAccessToken?.();

        // Log if flow was interrupted
        if (this.flowLogger) {
            this.flowLogger.log(AIImageFlowStep.FLOW_COMPLETED, {
                result: 'COMPONENT_DESTROYED'
            });
        }
	}
}
</script>
<style scoped>
.ai-image-website-iframe {
    position: fixed;
    top: 0;
    height: 100%;
    width: 100%;
    left: 0;
    z-index: 9999;
}</style>
